import React, { Dispatch, useState } from 'react';
import { useIntl } from 'react-intl';

import { Checkmark, Pencil } from '@xsolla/ui-icons';

import { setUserPhone } from '../../../../api';
import { useNotifications } from '../../../../hooks/useNotifications';
import { validatePhone } from '../../../../utils';
import { ExtraInputButton, PhoneInput } from '../../../ui';
import { IconName } from '../../../ui/icons/types';
import { Action, ActionType, UserState } from '../types';

type Props = {
  state: UserState;
  projectId: string;
  dispatch: Dispatch<Action>;
};

const ChangePhone = ({ state, projectId, dispatch }: Props): JSX.Element => {
  const intl = useIntl();
  const { showErrorNotify, showSuccessNotify } = useNotifications();
  const [currCountry, setCurrCountry] = useState<IconName>('US');

  const { phoneEdit, newPhone, user, phoneError, newPhoneFetching, isXsollaStorage } = state;

  const setPhoneHandle = async (phone: string) => {
    if (!user || !user.id) return;
    try {
      dispatch({ type: ActionType.changePhoneStart });
      await setUserPhone(projectId, user.id, {
        phone_number: phone
      });

      showSuccessNotify();
    } catch (err) {
      showErrorNotify(err);
    } finally {
      dispatch({ type: ActionType.changePhoneEnd });
    }
  };

  return (
    <PhoneInput
      name="phone"
      value={newPhone}
      readOnly={!phoneEdit}
      maxLength={20}
      afterChange={(value, country) => {
        setCurrCountry(country);
        dispatch({ type: ActionType.changePhone, payload: value });
      }}
      extraRight={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <ExtraInputButton
          type="button"
          palette="muted"
          disabled={(phoneEdit && newPhone === user.phone_auth) || !isXsollaStorage}
          fetching={newPhoneFetching}
          onClick={() => {
            if (phoneEdit && !validatePhone(newPhone, currCountry)) {
              dispatch({
                type: ActionType.changePhoneError,
                payload: 'app.validation.phone-invalid'
              });
              return;
            }
            if (phoneEdit) {
              setPhoneHandle(newPhone);
            }
            dispatch({ type: ActionType.phoneEditToggle });
          }}
          data-test-id="user-change-phone">
          {phoneEdit ? <Checkmark /> : <Pencil />}
        </ExtraInputButton>
      }
      label={intl.messages['users.settings.contact-info.primary-phone.label']}
      errorMessage={phoneEdit ? phoneError && intl.formatMessage({ id: phoneError }) : ''}
    />
  );
};

export default ChangePhone;
